// Shared helper: maps the `missing[]` returned by `russiaSendMissing` (or
// read from `ApiClientError.meta.missing` on a DRAFT_INCOMPLETE gate) to
// the i18n message key shown in the toast (Part A) and the proactive
// readiness hint (Part B). Keeping both consumers in sync.
//
// Keys are scoped to the `russiaRequests` namespace — pass them to a
// `t` obtained from `useTranslation('russiaRequests')`.

/** i18n message key type (scoped within the `russiaRequests` namespace). */
export type SendReadinessMessageKey =
  | 'drawer.sendReadiness.missingEndAddress'
  | 'drawer.sendReadiness.missingTotals'
  | 'drawer.sendReadiness.missingItems';

/**
 * The only keys the backend DRAFT_INCOMPLETE gate is allowed to emit.
 * Anything outside this set is treated as a malformed payload and falls
 * through to the generic error path rather than showing a precise toast.
 */
export const SEND_READINESS_ALLOWED_KEYS = [
  'endAddress',
  'items',
  'totalWeight',
  'totalVolume',
] as const;

/**
 * Filters a raw `meta.missing` value (untrusted JSON) to the allowed
 * send-readiness keys. Returns only elements that are strings and are
 * members of SEND_READINESS_ALLOWED_KEYS; an empty array signals a
 * malformed/unknown payload.
 */
export function narrowMissing(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (v): v is string =>
      typeof v === 'string' && (SEND_READINESS_ALLOWED_KEYS as readonly string[]).includes(v),
  );
}

/**
 * Maps a `missing[]` array (from `russiaSendMissing` or
 * `ApiClientError.meta.missing`) to the SINGLE most actionable i18n
 * message key. Priority (#1579): the required delivery address first,
 * then the totals, then positions.
 *
 * - Any entry of `'endAddress'` → missingEndAddress
 * - Else any entry of `'totalWeight'` / `'totalVolume'` → missingTotals
 * - Otherwise (the `'items'` entry) → missingItems
 */
export function missingToMessageKey(missing: string[]): SendReadinessMessageKey {
  if (missing.some((k) => k === 'endAddress')) {
    return 'drawer.sendReadiness.missingEndAddress';
  }
  if (missing.some((k) => k === 'totalWeight' || k === 'totalVolume')) {
    return 'drawer.sendReadiness.missingTotals';
  }
  return 'drawer.sendReadiness.missingItems';
}
